// pages/public/BuildingPage.tsx
/**
 * @description Main QR code landing page - shows building info and available units
 * Design System: Warm Sophistication - terracotta accent, warm neutrals, Fraunces/Outfit
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, DollarSign, Loader2 } from 'lucide-react';
import { PublicLayout } from '../../components/layout';
import {
  BuildingHeader,
  ScoreSection,
  AmenityList,
  LocationMap,
} from '../../components/building';
import { UnitCard } from '../../components/unit';
import {
  StickyFooter,
  SavedUnitsDrawer,
  BeauxBot,
  AffordabilityModal,
  QuickApplicationModal,
  WaitlistForm
} from '../../components/public';
import { getBuilding, trackEvent, type Building, type Unit } from '../../lib/api';

export function BuildingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Data state
  const [building, setBuilding] = useState<Building | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI State
  const [isSavedDrawerOpen, setIsSavedDrawerOpen] = useState(false);
  const [isAffordabilityOpen, setIsAffordabilityOpen] = useState(false);
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [budgetFilter, setBudgetFilter] = useState<number | null>(null);

  // Fetch building data from API
  useEffect(() => {
    async function fetchBuilding() {
      if (!slug) return;

      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getBuilding(slug);

      if (fetchError) {
        setError(fetchError);
        setBuilding(null);
        setUnits([]);
      } else if (data) {
        setBuilding(data.building);
        // Filter to only available units
        setUnits(data.units.filter(u => u.is_available));
      }

      setLoading(false);
    }

    fetchBuilding();
  }, [slug]);

  // Track QR scan on page load
  useEffect(() => {
    if (building?.id) {
      trackEvent('qr_scan', building.id);
    }
  }, [building?.id]);

  // Filter units by budget if set
  const filteredUnits = budgetFilter
    ? units.filter(u => u.price <= budgetFilter)
    : units;

  const handleViewDetails = (unit: Unit) => {
    navigate(`/b/${slug}/unit/${unit.id}`);
  };

  const handleFilterByBudget = (maxRent: number) => {
    setBudgetFilter(maxRent);
  };

  const clearBudgetFilter = () => {
    setBudgetFilter(null);
  };

  if (loading) {
    return (
      <PublicLayout showFooterPadding>
        <div className="flex items-center justify-center min-h-screen bg-bg-base">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-text-secondary font-body">Loading building...</span>
        </div>
      </PublicLayout>
    );
  }

  if (error || !building) {
    return (
      <PublicLayout showFooterPadding={false}>
        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-bg-base">
          <h1 className="font-display text-display-lg text-text mb-3">
            Building Not Found
          </h1>
          <p className="text-body-md text-text-secondary mb-6">
            The building you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/" className="text-primary font-medium hover:underline flex items-center gap-1">
            ← Back to all buildings
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout showFooterPadding>
      {/* Hero Header */}
      <BuildingHeader building={building as any} />

      <main className="flex flex-col gap-6 pb-24 bg-bg-base">
        {/* Walk Scores */}
        <div className="pt-6">
          <ScoreSection
            walkScore={building.walk_score}
            walkScoreDescription={building.walk_score_description}
            transitScore={building.transit_score}
            transitScoreDescription={building.transit_score_description}
            bikeScore={building.bike_score}
            bikeScoreDescription={building.bike_score_description}
          />
        </div>

        {/* Available Units */}
        <section className="px-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-display-sm text-text">
              {units.length === 0 ? (
                <>Availability</>
              ) : budgetFilter ? (
                <>Units Under ${budgetFilter.toLocaleString()}/mo</>
              ) : (
                <>Available Units <span className="text-text-muted">({filteredUnits.length})</span></>
              )}
            </h2>
            {budgetFilter && (
              <button
                onClick={clearBudgetFilter}
                className="text-body-sm text-primary font-medium hover:underline"
              >
                Show all
              </button>
            )}
          </div>

          {filteredUnits.length === 0 ? (
            // Show waitlist form if building is fully leased (no available units at all)
            units.length === 0 ? (
              <WaitlistForm buildingId={building.id} buildingName={building.name} />
            ) : (
              // Show budget filter message if units exist but none match budget
              <div className="text-center py-8 bg-surface rounded-lg border border-border">
                <p className="text-text-secondary text-body-md">No units match your budget.</p>
                <button
                  onClick={clearBudgetFilter}
                  className="text-primary font-medium mt-2 text-body-md hover:underline"
                >
                  Show all units
                </button>
              </div>
            )
          ) : (
            <div className="space-y-3">
              {filteredUnits.map((unit, index) => (
                <div
                  key={unit.id}
                  className="animate-reveal"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <UnitCard
                    unit={unit as any}
                    onViewDetails={() => handleViewDetails(unit)}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Affordability Calculator CTA - only show if units are available */}
        {units.length > 0 && (
          <section className="px-4">
            <button
              onClick={() => setIsAffordabilityOpen(true)}
              className="w-full p-4 bg-surface hover:bg-surface-hover border border-border rounded-lg flex items-center justify-between transition-all duration-fast hover:border-border-strong group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-subtle rounded-lg flex items-center justify-center">
                  <DollarSign size={20} className="text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-text text-body-md">Can I afford this?</p>
                  <p className="text-body-sm text-text-muted">Check your budget</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-text-muted group-hover:text-primary transition-colors" />
            </button>
          </section>
        )}

        {/* Amenities */}
        <section className="px-4">
          <AmenityList amenities={building.amenities} />
        </section>

        {/* Location Map */}
        <section className="px-4">
          <LocationMap building={building as any} />
        </section>

      </main>

      {/* Modals & Drawers */}
      <SavedUnitsDrawer
        isOpen={isSavedDrawerOpen}
        onClose={() => setIsSavedDrawerOpen(false)}
      />

      <AffordabilityModal
        isOpen={isAffordabilityOpen}
        onClose={() => setIsAffordabilityOpen(false)}
        units={units as any}
        onFilterByBudget={handleFilterByBudget}
      />

      <QuickApplicationModal
        isOpen={isApplicationOpen}
        onClose={() => setIsApplicationOpen(false)}
        building={{
          id: building.id,
          name: building.name,
          address: building.address,
          email: building.leasing_email
        }}
      />

      {/* Beaux Bot */}
      <BeauxBot />

      {/* Sticky Footer */}
      <StickyFooter
        buildingName={building.name}
        buildingEmail={building.leasing_email}
        onOpenSaved={() => setIsSavedDrawerOpen(true)}
        onApplyClick={() => setIsApplicationOpen(true)}
      />
    </PublicLayout>
  );
}
